<template>
  <div
    class="feature-mosaic-grid"
    :aria-label="ariaLabel"
    :style="rootStyle"
    data-text-reveal="off"
  >
    <div v-if="isIndustrialLayout" class="feature-mosaic-grid__desktop feature-mosaic-grid__desktop--industrial" role="list">
      <div class="feature-mosaic-grid__industrial-row feature-mosaic-grid__industrial-row--top">
        <FeatureMosaicCard
          v-if="desktopHero"
          :item="desktopHero"
          role="listitem"
        />

        <div class="feature-mosaic-grid__industrial-right">
          <FeatureMosaicCard
            v-for="item in getGroupItems('topFull')"
            :key="item.key"
            :item="item"
            role="listitem"
          />

          <div class="feature-mosaic-grid__industrial-top-cards">
            <FeatureMosaicCard
              v-for="item in getGroupItems('topCards')"
              :key="item.key"
              :item="item"
              role="listitem"
            />
          </div>
        </div>
      </div>

      <div
        v-if="hasBottomRow"
        class="feature-mosaic-grid__industrial-row feature-mosaic-grid__industrial-row--bottom"
      >
        <FeatureMosaicCard
          v-for="item in getGroupItems('bottomLead')"
          :key="item.key"
          :item="item"
          role="listitem"
        />

        <div
          v-for="stack in desktopStacks"
          :key="stack.key"
          class="feature-mosaic-grid__photo-stack"
        >
          <FeatureMosaicCard
            v-for="item in stack.items"
            :key="item.key"
            :item="item"
            role="listitem"
          />
        </div>

        <FeatureMosaicCard
          v-for="item in getGroupItems('bottomTrail')"
          :key="item.key"
          :item="item"
          role="listitem"
        />
      </div>
    </div>

    <div v-else-if="isMechanicalLayout" class="feature-mosaic-grid__desktop feature-mosaic-grid__desktop--mechanical" role="list">
      <div class="feature-mosaic-grid__mechanical-row">
        <FeatureMosaicCard
          v-if="desktopHero"
          :item="desktopHero"
          role="listitem"
        />

        <div class="feature-mosaic-grid__mechanical-middle">
          <FeatureMosaicCard
            v-for="item in getGroupItems('topFull')"
            :key="item.key"
            :item="item"
            role="listitem"
          />

          <div class="feature-mosaic-grid__mechanical-cards">
            <FeatureMosaicCard
              v-for="item in getGroupItems('topCards')"
              :key="item.key"
              :item="item"
              role="listitem"
            />
          </div>
        </div>

        <div class="feature-mosaic-grid__mechanical-tall">
          <FeatureMosaicCard
            v-for="item in getGroupItems('topNarrow')"
            :key="item.key"
            :item="item"
            role="listitem"
          />
        </div>
      </div>
    </div>

    <div v-else class="feature-mosaic-grid__desktop" role="list">
      <div class="feature-mosaic-grid__desktop-row feature-mosaic-grid__desktop-row--top">
        <FeatureMosaicCard
          v-if="desktopHero"
          :item="desktopHero"
          role="listitem"
        />

        <div class="feature-mosaic-grid__desktop-right">
          <div class="feature-mosaic-grid__desktop-column feature-mosaic-grid__desktop-column--wide">
            <FeatureMosaicCard
              v-for="item in getGroupItems('topWide')"
              :key="item.key"
              :item="item"
              role="listitem"
            />
          </div>

          <div class="feature-mosaic-grid__desktop-column feature-mosaic-grid__desktop-column--narrow">
            <FeatureMosaicCard
              v-for="item in getGroupItems('topNarrow')"
              :key="item.key"
              :item="item"
              role="listitem"
            />
          </div>
        </div>
      </div>

      <div
        v-if="hasBottomRow"
        class="feature-mosaic-grid__desktop-row feature-mosaic-grid__desktop-row--bottom"
      >
        <FeatureMosaicCard
          v-for="item in getGroupItems('bottomLead')"
          :key="item.key"
          :item="item"
          role="listitem"
        />

        <div
          v-for="stack in desktopStacks"
          :key="stack.key"
          class="feature-mosaic-grid__photo-stack"
        >
          <FeatureMosaicCard
            v-for="item in stack.items"
            :key="item.key"
            :item="item"
            role="listitem"
          />
        </div>

        <FeatureMosaicCard
          v-for="item in getGroupItems('bottomTrail')"
          :key="item.key"
          :item="item"
          role="listitem"
        />
      </div>
    </div>

    <div v-if="isIndustrialLayout" class="feature-mosaic-grid__mobile feature-mosaic-grid__mobile--industrial" role="list">
      <FeatureMosaicCard
        v-if="mobileHero"
        :item="mobileHero"
        compact
        role="listitem"
      />

      <div
        v-if="getGroupItems('topFull').length || getGroupItems('topCards').length"
        class="feature-mosaic-grid__industrial-mobile-panel feature-mosaic-grid__industrial-mobile-panel--top"
      >
        <FeatureMosaicCard
          v-for="item in getGroupItems('topFull')"
          :key="item.key"
          :item="item"
          compact
          role="listitem"
        />

        <div class="feature-mosaic-grid__industrial-mobile-top-cards">
          <FeatureMosaicCard
            v-for="item in getGroupItems('topCards')"
            :key="item.key"
            :item="item"
            compact
            role="listitem"
          />
        </div>
      </div>

      <div
        v-if="hasBottomRow"
        class="feature-mosaic-grid__industrial-mobile-panel feature-mosaic-grid__industrial-mobile-panel--metrics"
      >
        <div class="feature-mosaic-grid__industrial-mobile-metric-row">
          <FeatureMosaicCard
            v-for="item in getGroupItems('bottomLead')"
            :key="item.key"
            :item="item"
            compact
            role="listitem"
          />

          <div class="feature-mosaic-grid__photo-stack">
            <FeatureMosaicCard
              v-for="item in getGroupItems('bottomLeftStack')"
              :key="item.key"
              :item="item"
              compact
              role="listitem"
            />
          </div>
        </div>

        <div class="feature-mosaic-grid__industrial-mobile-metric-row">
          <div class="feature-mosaic-grid__photo-stack">
            <FeatureMosaicCard
              v-for="item in getGroupItems('bottomRightStack')"
              :key="item.key"
              :item="item"
              compact
              role="listitem"
            />
          </div>

          <FeatureMosaicCard
            v-for="item in getGroupItems('bottomTrail')"
            :key="item.key"
            :item="item"
            compact
            role="listitem"
          />
        </div>
      </div>
    </div>

    <div v-else-if="isMechanicalLayout" class="feature-mosaic-grid__mobile feature-mosaic-grid__mobile--mechanical" role="list">
      <FeatureMosaicCard
        v-if="mobileHero"
        :item="mobileHero"
        compact
        role="listitem"
      />

      <div class="feature-mosaic-grid__mechanical-mobile-panel">
        <div class="feature-mosaic-grid__mechanical-middle">
          <FeatureMosaicCard
            v-for="item in getGroupItems('topFull')"
            :key="item.key"
            :item="item"
            compact
            role="listitem"
          />

          <div class="feature-mosaic-grid__mechanical-cards">
            <FeatureMosaicCard
              v-for="item in getGroupItems('topCards')"
              :key="item.key"
              :item="item"
              compact
              role="listitem"
            />
          </div>
        </div>

        <div class="feature-mosaic-grid__mechanical-tall">
          <FeatureMosaicCard
            v-for="item in getGroupItems('topNarrow')"
            :key="item.key"
            :item="item"
            compact
            role="listitem"
          />
        </div>
      </div>
    </div>

    <div v-else class="feature-mosaic-grid__mobile" role="list">
      <FeatureMosaicCard
        v-if="mobileHero"
        :item="mobileHero"
        compact
        role="listitem"
      />

      <div
        v-if="mobileMetricLead || mobileMetricStackItems.length"
        class="feature-mosaic-grid__mobile-panel feature-mosaic-grid__mobile-panel--metrics"
      >
        <FeatureMosaicCard
          v-if="mobileMetricLead"
          class="feature-mosaic-grid__mobile-metric-lead"
          :item="mobileMetricLead"
          compact
          role="listitem"
        />

        <div class="feature-mosaic-grid__mobile-stack feature-mosaic-grid__mobile-stack--metrics">
          <FeatureMosaicCard
            v-for="item in mobileMetricStackItems"
            :key="item.key"
            :item="item"
            compact
            role="listitem"
          />
        </div>
      </div>

      <div
        v-if="hasMobileCapabilityPanel"
        class="feature-mosaic-grid__mobile-panel feature-mosaic-grid__mobile-panel--capability"
      >
        <div class="feature-mosaic-grid__mobile-stack feature-mosaic-grid__mobile-stack--top-left">
          <FeatureMosaicCard
            v-for="item in mobileTopLeftItems"
            :key="item.key"
            :item="item"
            compact
            role="listitem"
          />
        </div>

        <div class="feature-mosaic-grid__mobile-stack feature-mosaic-grid__mobile-stack--top-right">
          <FeatureMosaicCard
            v-for="item in mobileTopRightItems"
            :key="item.key"
            :item="item"
            compact
            role="listitem"
          />
        </div>

        <div class="feature-mosaic-grid__mobile-stack feature-mosaic-grid__mobile-stack--bottom-left">
          <FeatureMosaicCard
            v-for="item in mobileBottomLeftItems"
            :key="item.key"
            :item="item"
            compact
            role="listitem"
          />
        </div>

        <FeatureMosaicCard
          v-if="mobileStatsItem"
          class="feature-mosaic-grid__mobile-stats"
          :item="mobileStatsItem"
          compact
          role="listitem"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import FeatureMosaicCard from './FeatureMosaicCard.vue'
import type { FeatureMosaicGroup, FeatureMosaicItem } from './featureMosaic'

const props = withDefaults(defineProps<{
  items: FeatureMosaicItem[]
  ariaLabel?: string
  layout?: 'publicSecurity' | 'industrial' | 'mechanical'
  mobileSplitIndex?: number
  gap?: string
  cardRadius?: string
  compactCardRadius?: string
  bottomHeight?: string
}>(), {
  ariaLabel: '',
  layout: 'publicSecurity',
  mobileSplitIndex: 0,
  gap: 'var(--police-feature-gap, var(--pixso-gap-md))',
  cardRadius: 'var(--police-card-radius, var(--pixso-radius-lg))',
  compactCardRadius: 'var(--police-card-radius-compact, var(--pixso-radius-md))',
  bottomHeight: 'var(--police-feature-bottom-height, 514px)'
})

const rootStyle = computed(() => ({
  '--feature-mosaic-gap-base': props.gap,
  '--feature-mosaic-card-radius': props.cardRadius,
  '--feature-mosaic-card-radius-compact': props.compactCardRadius,
  '--feature-mosaic-bottom-height': props.bottomHeight
}))

const isIndustrialLayout = computed(() => props.layout === 'industrial')
const isMechanicalLayout = computed(() => props.layout === 'mechanical')

const groupedItems = computed(() => props.items.reduce<Record<FeatureMosaicGroup, FeatureMosaicItem[]>>((groups, item) => {
  const group = item.group || (item.variant === 'hero' ? 'hero' : 'topWide')
  groups[group].push(item)
  return groups
}, {
  hero: [],
  topWide: [],
  topNarrow: [],
  topFull: [],
  topCards: [],
  bottomLead: [],
  bottomLeftStack: [],
  bottomRightStack: [],
  bottomTrail: []
}))

const getGroupItems = (group: FeatureMosaicGroup) => groupedItems.value[group]

const desktopHero = computed(() => getGroupItems('hero')[0] || props.items[0])
const mobileHero = computed(() => desktopHero.value)

const mobileMetricLead = computed(() => getGroupItems('bottomLead')[0])
const mobileMetricStackItems = computed(() => getGroupItems('bottomLeftStack'))
const mobileTopLeftItems = computed(() => getGroupItems('topWide'))
const mobileTopRightItems = computed(() => getGroupItems('topNarrow'))
const mobileBottomLeftItems = computed(() => getGroupItems('bottomRightStack'))
const mobileStatsItem = computed(() => getGroupItems('bottomTrail')[0])
const hasBottomRow = computed(() => (
  getGroupItems('bottomLead').length > 0 ||
  getGroupItems('bottomLeftStack').length > 0 ||
  getGroupItems('bottomRightStack').length > 0 ||
  getGroupItems('bottomTrail').length > 0
))
const hasMobileCapabilityPanel = computed(() => (
  mobileTopLeftItems.value.length > 0 ||
  mobileTopRightItems.value.length > 0 ||
  mobileBottomLeftItems.value.length > 0 ||
  Boolean(mobileStatsItem.value)
))

const desktopStacks = computed(() => [
  { key: 'bottom-left-stack', items: getGroupItems('bottomLeftStack') },
  { key: 'bottom-right-stack', items: getGroupItems('bottomRightStack') }
].filter((stack) => stack.items.length > 0))
</script>

<style scoped>
.feature-mosaic-grid {
  width: 100%;
  --feature-mosaic-gap: var(--feature-mosaic-gap-base);
}

.feature-mosaic-grid__desktop {
  display: flex;
  flex-direction: column;
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__desktop-row {
  display: grid;
  gap: var(--feature-mosaic-gap);
  align-items: stretch;
}

.feature-mosaic-grid__desktop-row--top {
  grid-template-columns: minmax(0, 884fr) minmax(0, 884fr);
}

.feature-mosaic-grid__desktop-row--bottom {
  grid-template-columns: minmax(180px, 320fr) minmax(0, 540fr) minmax(0, 540fr) minmax(180px, 320fr);
  height: var(--feature-mosaic-bottom-height);
}

.feature-mosaic-grid__desktop-right {
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 540fr) minmax(180px, 320fr);
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__desktop-column {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__desktop-column--wide :deep(.feature-mosaic-card--image) {
  aspect-ratio: 540 / 387;
}

.feature-mosaic-grid__desktop-column--wide :deep(.feature-mosaic-card--action) {
  flex: 1 1 0;
}

.feature-mosaic-grid__desktop-column--narrow :deep(.feature-mosaic-card) {
  flex: 1 1 0;
}

.feature-mosaic-grid__desktop-column--narrow :deep(.feature-mosaic-card--voice) {
  display: block;
}

.feature-mosaic-grid__desktop-row--bottom :deep(.feature-mosaic-card) {
  min-height: 0;
}

.feature-mosaic-grid__photo-stack {
  min-width: 0;
  display: grid;
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__desktop--industrial {
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__industrial-row {
  display: grid;
  gap: var(--feature-mosaic-gap);
  align-items: stretch;
}

.feature-mosaic-grid__industrial-row--top {
  grid-template-columns: minmax(0, 884fr) minmax(0, 884fr);
}

.feature-mosaic-grid__industrial-row--bottom {
  grid-template-columns: minmax(180px, 320fr) minmax(0, 540fr) minmax(0, 540fr) minmax(180px, 320fr);
  height: var(--feature-mosaic-bottom-height);
}

.feature-mosaic-grid__industrial-row--bottom :deep(.feature-mosaic-card) {
  min-height: 0;
}

.feature-mosaic-grid__industrial-right {
  min-width: 0;
  display: grid;
  grid-template-rows: 323fr 283fr;
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__industrial-top-cards {
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__desktop--industrial :deep(.feature-mosaic-card--group-topFull) {
  min-height: 0;
}

.feature-mosaic-grid__desktop--industrial :deep(.feature-mosaic-card--group-topCards) {
  min-height: 0;
}

.feature-mosaic-grid__desktop--mechanical {
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__mechanical-row {
  display: grid;
  grid-template-columns: minmax(0, 884fr) minmax(0, 564fr) minmax(0, 320fr);
  gap: var(--feature-mosaic-gap);
  align-items: stretch;
}

.feature-mosaic-grid__mechanical-middle {
  min-width: 0;
  min-height: 0;
  /* Keep min-content from raising row height above the hero (avoids truck overlap ~1201–1280). */
  overflow: hidden;
  display: grid;
  grid-template-rows: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__mechanical-cards {
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--feature-mosaic-gap);
}

.feature-mosaic-grid__mechanical-tall {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.feature-mosaic-grid__desktop--mechanical :deep(.feature-mosaic-card--hero) {
  /* Size height from column width only — stretched height + aspect-ratio was inflating width. */
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 0;
  height: auto;
  aspect-ratio: 884 / 630;
  align-self: start;
  justify-self: stretch;
}

.feature-mosaic-grid__desktop--mechanical :deep(.feature-mosaic-card--group-topFull),
.feature-mosaic-grid__desktop--mechanical :deep(.feature-mosaic-card--group-topCards) {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.feature-mosaic-grid__desktop--mechanical :deep(.feature-mosaic-card--group-topNarrow) {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.feature-mosaic-grid__mobile {
  display: none;
}

@media (max-width: 1200px) {
  .feature-mosaic-grid {
    --feature-mosaic-gap: clamp(8px, 1vw, var(--pixso-gap-sm));
  }

  .feature-mosaic-grid__desktop {
    display: none;
  }

  .feature-mosaic-grid__mobile {
    display: grid;
    gap: var(--feature-mosaic-gap);
  }

  .feature-mosaic-grid__mobile--industrial,
  .feature-mosaic-grid__mobile--mechanical {
    display: grid;
  }

  /* Mechanical mobile: hero full-width, then same middle+tall mosaic as desktop */
  .feature-mosaic-grid__mechanical-mobile-panel {
    min-width: 0;
    display: grid;
    grid-template-columns: minmax(0, 564fr) minmax(0, 320fr);
    gap: var(--feature-mosaic-gap);
    align-items: stretch;
    /* Keep panel proportions aligned with desktop right cluster */
    aspect-ratio: 884 / 630;
  }

  .feature-mosaic-grid__mobile--mechanical .feature-mosaic-grid__mechanical-middle,
  .feature-mosaic-grid__mobile--mechanical .feature-mosaic-grid__mechanical-cards,
  .feature-mosaic-grid__mobile--mechanical .feature-mosaic-grid__mechanical-tall {
    min-width: 0;
    min-height: 0;
  }

  .feature-mosaic-grid__mobile--mechanical .feature-mosaic-grid__mechanical-middle {
    display: grid;
    grid-template-rows: minmax(0, 1.15fr) minmax(0, 1fr);
    gap: var(--feature-mosaic-gap);
  }

  .feature-mosaic-grid__mobile--mechanical .feature-mosaic-grid__mechanical-cards {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: var(--feature-mosaic-gap);
  }

  .feature-mosaic-grid__mobile--mechanical .feature-mosaic-grid__mechanical-tall {
    display: flex;
    flex-direction: column;
  }

  .feature-mosaic-grid__mobile--mechanical :deep(.feature-mosaic-card--hero) {
    aspect-ratio: 884 / 630;
  }

  .feature-mosaic-grid__mobile--mechanical :deep(.feature-mosaic-card--group-topFull),
  .feature-mosaic-grid__mobile--mechanical :deep(.feature-mosaic-card--group-topCards) {
    min-height: 0;
  }

  .feature-mosaic-grid__mobile--mechanical :deep(.feature-mosaic-card--group-topNarrow) {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
  }

  .feature-mosaic-grid__industrial-mobile-panel,
  .feature-mosaic-grid__industrial-mobile-top-cards,
  .feature-mosaic-grid__industrial-mobile-metric-row {
    min-width: 0;
    display: grid;
    gap: var(--feature-mosaic-gap);
  }

  .feature-mosaic-grid__industrial-mobile-panel--top {
    grid-template-columns: 1fr;
  }

  .feature-mosaic-grid__industrial-mobile-top-cards {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: stretch;
  }

  .feature-mosaic-grid__industrial-mobile-panel--metrics {
    grid-template-columns: 1fr;
  }

  .feature-mosaic-grid__industrial-mobile-metric-row {
    /* Match desktop lead:stack proportions so photo cards stay wider than
     * the tall metric cards and avoid sub-pixel side gutters. */
    grid-template-columns: minmax(0, 320fr) minmax(0, 540fr);
    align-items: stretch;
  }

  .feature-mosaic-grid__industrial-mobile-metric-row:nth-child(2) {
    grid-template-columns: minmax(0, 540fr) minmax(0, 320fr);
  }

  .feature-mosaic-grid__mobile--industrial :deep(.feature-mosaic-card--hero) {
    aspect-ratio: 884 / 630;
  }

  .feature-mosaic-grid__mobile--industrial :deep(.feature-mosaic-card--group-topFull) {
    min-height: 0;
    aspect-ratio: 884 / 323;
  }

  .feature-mosaic-grid__mobile--industrial :deep(.feature-mosaic-card--group-topCards) {
    min-height: 0;
    aspect-ratio: 278.666 / 283;
  }

  .feature-mosaic-grid__mobile--industrial :deep(.feature-mosaic-card--group-bottomLead),
  .feature-mosaic-grid__mobile--industrial :deep(.feature-mosaic-card--group-bottomTrail) {
    /* Lead/trail set the metric-row height from the desktop 320×514 ratio. */
    min-width: 0;
    min-height: 0;
    width: 100%;
    aspect-ratio: 320 / 514;
  }

  .feature-mosaic-grid__mobile--industrial :deep(.feature-mosaic-card--group-bottomLeftStack),
  .feature-mosaic-grid__mobile--industrial :deep(.feature-mosaic-card--group-bottomRightStack) {
    /* Fill the photo-stack track. A fixed 540/245 ratio transfers width from
     * the stretched height and leaves a thin empty gutter beside photo cards
     * (e.g. temperature) on common mobile widths. */
    min-width: 0;
    min-height: 0;
    width: 100%;
    height: 100%;
    aspect-ratio: auto;
  }

  .feature-mosaic-grid__mobile--industrial .feature-mosaic-grid__photo-stack {
    min-width: 0;
    min-height: 0;
    width: 100%;
    height: 100%;
    align-self: stretch;
  }

  .feature-mosaic-grid__mobile-panel,
  .feature-mosaic-grid__mobile-stack {
    min-width: 0;
    display: grid;
    gap: var(--feature-mosaic-gap);
  }

  .feature-mosaic-grid__mobile-panel--metrics {
    grid-template-columns: minmax(0, 320fr) minmax(0, 540fr);
    align-items: stretch;
  }

  .feature-mosaic-grid__mobile-panel--metrics :deep(.feature-mosaic-card--photo) {
    min-height: 0;
    aspect-ratio: 540 / 320;
  }

  .feature-mosaic-grid__mobile-metric-lead {
    min-height: 0;
    height: 100%;
  }

  .feature-mosaic-grid__mobile-panel--capability {
    grid-template-columns: minmax(0, 540fr) minmax(0, 320fr);
    grid-template-areas:
      "top-left top-right"
      "bottom-left stats";
    align-items: stretch;
  }

  .feature-mosaic-grid__mobile-stack--top-left {
    grid-area: top-left;
  }

  .feature-mosaic-grid__mobile-stack--top-right {
    grid-area: top-right;
  }

  .feature-mosaic-grid__mobile-stack--bottom-left {
    grid-area: bottom-left;
  }

  .feature-mosaic-grid__mobile-stats {
    grid-area: stats;
    min-height: 0;
    height: 100%;
  }

  .feature-mosaic-grid__mobile-stack--top-left :deep(.feature-mosaic-card--ai) {
    min-height: 0;
    aspect-ratio: 540 / 387;
  }

  .feature-mosaic-grid__mobile-stack--top-left :deep(.feature-mosaic-card--action) {
    min-height: 0;
    aspect-ratio: 540 / 219;
  }

  .feature-mosaic-grid__mobile-stack--top-right :deep(.feature-mosaic-card) {
    min-height: 0;
    aspect-ratio: 320 / 303;
  }

  .feature-mosaic-grid__mobile-stack--bottom-left :deep(.feature-mosaic-card--photo) {
    min-height: 0;
    aspect-ratio: 540 / 320;
  }
}

@media (max-width: 768px) {
  .feature-mosaic-grid {
    --feature-mosaic-gap: clamp(6px, 1.6vw, 8px);
  }
}

</style>
